import React from 'react';
import { Col } from 'antd';
import { useTranslation } from 'react-i18next';
import FormItem from 'src/components/form-item';
import DoubleArrowImage from 'src/assets/img/double-arrow.svg';
import TableContent from 'src/components/table-content';
import './style.scss';
import AvatarDescription from 'src/components/avatar-description';

/**
 * RollbackItem component - used in rollbackModal in Deallocation and Need pages.
 * 
 * @param {Object} props - Props for the PageTitle component.
 * @param {Object} props.matchPeople - Deallocation object (if it is on people page, it already contains need data).
 * @param {string} props.type - Text indicating if it is for 'people' page or 'need' page.
 * @param {Object} props.need - If it is on Need page, get need object as property.
 * @returns {JSX.Element} The RollbackItem component.
 * @example
 *  <RollbackItem matchPeople={matchPeople} type="people" />
 *  // OR
 *  <RollbackItem matchPeople={matchPeople} need={need} type="need" />
 */
function RollbackItem({ matchPeople, type, need }) {
    const { t } = useTranslation();

    const peopleData = {
        login: matchPeople[0].collaborator_deallocation.login,
        role: matchPeople[0].collaborator_deallocation.role,
        positionMap: matchPeople[0].collaborator_deallocation.positionMap
    };

    // different sources whether it's Deallocation's page or Need's page
    const needData = type === 'people' ? {
        title: matchPeople[0].needs[0].title,
        requisitionCode: `#${matchPeople[0].needs[0].requisitionCode}`,
        client: matchPeople[0].needs[0].client ?? '-',
    } : {
        title: need.title,
        requisitionCode: `#${need.requisitionCode}`,
        client: need.client ?? '-',
    };

    /**
     * Generates needItem container
     * @returns {JSX.Element} NeedItem
     */
    const needItem = () => (
        <FormItem label={t('needsTitle')} aria-label='opportunity'>
            <Col aria-label='opportunity'>
                <div className='rollback-container' aria-describedby='opportunity' tabIndex={0} >
                    <TableContent 
                        mainContent={`${needData.title} (${needData.requisitionCode})`}
                        subContent={`${t('client')}: ${needData.client ?? '-'}`}
                    />
                </div>
            </Col>
        </FormItem>
    )

    /**
     * Generates peopleItem container
     * @returns {JSX.Element}
     */
    const peopleItem = () => (
        <Col aria-label='profile'>
            <FormItem label={t('profile')}>
                <div className='rollback-container' aria-describedby='profile' tabIndex={0}>
                    <AvatarDescription
                        mainText={peopleData.login}
                        mainTextAriaLabel={peopleData.login}
                        mainTextTranslation={t('name')}
                        subText={[
                                {
                                    description: t('role'),
                                    value: peopleData.role,
                                },
                                {
                                    description: t('level'),
                                    value: peopleData.positionMap,
                                },
                            ]}
                        className='rollback-profile'
                    />
                </div>
            </FormItem>
        </Col>
    )

    return (
        type === 'people' ? 
            (
                <div className='people-item--flex'>
                {peopleItem()}
                <img src={DoubleArrowImage} alt='match arrow' className='arrow-image' />
                {needItem()}
            </div>
            ) :
            (
                <div className='people-item--flex'>
                {needItem()}
                <img src={DoubleArrowImage} alt='match arrow' className='arrow-image' />
                {peopleItem()}
                </div>
            )
    );
}

RollbackItem.defaultProps = {
    matchPeople: {},
    type: '', 
    need: {}
};

export default RollbackItem;
